import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck2, Check, FileLock2, FlaskConical, ListChecks, MapPin, Minus, Upload } from "lucide-react";
import { Logo } from "@/components/Logo";

const STEPS = [
  [Upload, "Save your property paperwork", "Upload tax bills, HOA notices, repair estimates and closing papers as PDF or text. Each property keeps its own files, privately."],
  [ListChecks, "Understand the forms", "Find official forms that may apply, see each question explained in plain language, and prepare your own answers."],
  [CalendarCheck2, "Confirm the due dates", "Add the dates that matter. Mark each one confirmed once you've checked it, so your list stays trustworthy."],
];

const READY = ["Sign-up, email confirmation and password reset", "Workspaces for homeowners and agents, with multiple properties", "Private document uploads and saved list",
  "Actions and confirmed deadlines you create", "A small library of verified Los Angeles-area and IRS forms, with private drafts", "Optional trademark and patent date records", "A practice sample workspace"];
const LATER = ["AI reading of documents and AI form help", "Automatic email reminders", "Payments and billing"];

const Nav = () => (
  <header className="absolute inset-x-0 top-0 z-20">
    <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
      <Logo light />
      <nav className="flex items-center gap-2 sm:gap-6 text-sm">
        <a href="#how" data-testid="nav-how-link" className="hidden text-white/75 hover:text-white sm:inline">How it works</a>
        <a href="#status" data-testid="nav-status-link" className="hidden text-white/75 hover:text-white sm:inline">What's ready</a>
        <Link to="/signin" data-testid="nav-signin-button" className="px-3 py-2 text-white/90 hover:text-white">Sign in</Link>
        <Link to="/signup" data-testid="nav-signup-button" className="rounded-full bg-gold px-4 py-2 font-medium text-navy transition-colors hover:bg-white">Get started</Link>
      </nav>
    </div>
  </header>
);

const Hero = () => (
  <section className="relative overflow-hidden bg-navy text-white grain">
    <Nav />
    <div className="mx-auto grid max-w-7xl gap-12 px-5 pb-20 pt-32 sm:px-8 lg:grid-cols-[1.15fr_1fr] lg:pb-28 lg:pt-40">
      <div>
        <p className="eyebrow text-gold fade-up">For homeowners and real estate agents</p>
        <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] fade-up" style={{ animationDelay: "80ms" }}>
          Paperwork you understand.<br /><span className="text-gold">Deadlines you've confirmed.</span>
        </h1>
        <p className="mt-6 max-w-xl text-base sm:text-lg text-white/75 leading-relaxed fade-up" style={{ animationDelay: "160ms" }}>
          EVR keeps property paperwork in one private place, explains official forms in plain language, and tracks the due dates you've confirmed.
        </p>
        <div className="mt-9 flex flex-wrap gap-3 fade-up" style={{ animationDelay: "240ms" }}>
          <Link to="/signup" data-testid="hero-signup-button" className="group inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-medium text-navy transition-colors hover:bg-white">
            Create an account <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <a href="#how" data-testid="hero-how-button" className="rounded-full border border-white/25 px-6 py-3 text-white/90 transition-colors hover:border-gold hover:text-gold">See how it works</a>
        </div>
      </div>
      <div className="relative fade-up" style={{ animationDelay: "200ms" }}>
        <div className="absolute -left-4 -top-4 h-full w-full rounded-2xl border border-gold/40" />
        <img src="/images/hero.jpg" alt="Property plans and paperwork on a desk" className="relative h-[340px] w-full rounded-2xl object-cover sm:h-[440px]" />
        <div className="absolute -bottom-6 left-6 right-6 rounded-xl border border-white/10 bg-navy/80 p-4 backdrop-blur-xl sm:left-auto sm:w-72">
          <p className="text-xs text-white/60">Example deadline</p>
          <p className="mt-1 font-medium">Property tax installment</p>
          <p className="mt-2 inline-flex rounded-full bg-emerald-400/15 px-2 py-0.5 text-xs text-emerald-300">Confirmed date</p>
        </div>
      </div>
    </div>
  </section>
);

const How = () => (
  <section id="how" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <p className="eyebrow text-gold-dark">How it works</p>
      <h2 className="mt-3 max-w-2xl text-3xl sm:text-4xl font-bold tracking-tight text-navy">Three plain steps. No jargon.</h2>
      <div className="mt-14 grid gap-px overflow-hidden rounded-2xl border bg-slate-200 md:grid-cols-3">
        {STEPS.map(([Icon, title, text], i) => (
          <div key={title} className="bg-white p-8 transition-colors hover:bg-paper" data-testid={`how-step-${i + 1}`}>
            <span className="font-mono text-sm text-gold-dark">0{i + 1}</span>
            <Icon className="mt-6 h-7 w-7 text-navy" />
            <h3 className="mt-4 text-xl font-semibold text-navy">{title}</h3>
            <p className="mt-2 text-slate-600 leading-relaxed">{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Trust = () => (
  <section className="bg-paper py-20 sm:py-28">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-3">
      {[[FileLock2, "Private by design", "Each workspace is locked to its own members. Agents never get access to a homeowner's workspace automatically. Files open only through your signed-in account."],
        [MapPin, "One place per property", "Add each home, listing or project with its city, state and county, so local forms and dates stay with the right property."],
        [FlaskConical, "Practice first", "Every account includes a clearly labeled sample workspace. Try things out without touching real records."]].map(([Icon, t, d]) => (
        <div key={t} className="border-l-2 border-gold pl-6">
          <Icon className="h-6 w-6 text-navy" />
          <h3 className="mt-4 text-xl font-semibold text-navy">{t}</h3>
          <p className="mt-2 text-slate-600 leading-relaxed">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const Status = () => (
  <section id="status" className="bg-white py-20 sm:py-28">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <p className="eyebrow text-gold-dark">Straight answers</p>
      <h2 className="mt-3 max-w-2xl text-3xl sm:text-4xl font-bold tracking-tight text-navy">What's ready today, and what isn't yet</h2>
      <div className="mt-12 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <div className="rounded-2xl border p-8" data-testid="status-ready-list">
          <h3 className="text-lg font-semibold text-navy">Ready now</h3>
          <ul className="mt-5 grid gap-3 sm:grid-cols-2">
            {READY.map((r) => <li key={r} className="flex gap-2 text-sm text-slate-700"><Check className="h-4 w-4 shrink-0 text-emerald-600" />{r}</li>)}
          </ul>
        </div>
        <div className="rounded-2xl border border-dashed border-slate-300 bg-paper p-8" data-testid="status-later-list">
          <h3 className="text-lg font-semibold text-navy">Not connected yet</h3>
          <ul className="mt-5 space-y-3">
            {LATER.map((r) => (
              <li key={r} className="flex items-center justify-between gap-3 text-sm text-slate-600">
                <span className="flex gap-2"><Minus className="h-4 w-4 shrink-0 text-slate-400" />{r}</span>
                <span className="shrink-0 rounded-full border px-2 py-0.5 text-[11px] text-slate-500">Coming later</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-slate-500">We'll turn these on only after they're connected and tested.</p>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-navy text-white">
    <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8">
      <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="max-w-lg text-3xl font-bold tracking-tight">Get your paperwork in order.</h2>
          <Link to="/signup" data-testid="footer-signup-button" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 font-medium text-navy hover:bg-white transition-colors">
            Create an account <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <Logo light testId="footer-logo" />
      </div>
      <p className="mt-12 border-t border-white/10 pt-6 text-xs text-white/50">© 2026 EVR Global Solutions. EVR helps you organize paperwork and prepare your own form answers. It does not give legal, tax or financial advice, and it never signs or submits forms for you.</p>
    </div>
  </footer>
);

export default function Landing() {
  return (
    <div data-testid="landing-page">
      <Hero /><How /><Trust /><Status /><Footer />
    </div>
  );
}
